import React, { useState } from 'react';
import './TrialBanner.css';

/**
 * Trial Banner Component
 * Shows trial status, restrictions, and upgrade button
 */
const TrialBanner = ({ trialStatus, onUpgrade }) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState(null);

  if (!trialStatus || !trialStatus.isOnTrial) {
    return null;
  }

  const { daysRemaining, limits, plan } = trialStatus;

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true);
    setError(null);
  };

  const handleConfirmUpgrade = async () => {
    try {
      setUpgrading(true);
      setError(null);
      await onUpgrade();
      setShowUpgradeModal(false);
      // Show success message
      alert('🎉 Upgrade successful! You now have full access to all features.');
    } catch (err) {
      setError(err.message || 'Upgrade failed. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <>
      <div className="trial-banner">
        <div className="trial-banner-content">
          <div className="trial-info">
            <div className="trial-icon">🎉</div>
            <div className="trial-text">
              <div className="trial-title">
                Free Trial - {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
              </div>
              <div className="trial-subtitle">
                {plan} Plan
              </div>
            </div>
          </div>

          <div className="trial-limits">
            <div className="limit-badge">
              <span className="limit-icon">👥</span>
              <span className="limit-text">{limits.attendees || 50} attendees max</span>
            </div>
            <div className="limit-badge">
              <span className="limit-icon">📸</span>
              <span className="limit-text">{limits.photosPerEvent || 100} photos/event max</span>
            </div>
            <div className="limit-badge">
              <span className="limit-icon">🔒</span>
              <span className="limit-text">No custom watermarks</span>
            </div>
          </div>

          <button 
            className="upgrade-button"
            onClick={handleUpgradeClick}
          >
            Upgrade Now - Get Full Access
          </button>
        </div>
      </div>

      {showUpgradeModal && (
        <div className="upgrade-modal-overlay" onClick={() => !upgrading && setShowUpgradeModal(false)}>
          <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upgrade to Full Access</h2>
              <button 
                className="modal-close"
                onClick={() => setShowUpgradeModal(false)}
                disabled={upgrading}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="upgrade-info">
                <div className="info-item">
                  <span className="info-icon">💳</span>
                  <div className="info-text">
                    <strong>You'll be charged immediately</strong>
                    <p>Your trial will end and billing starts today</p>
                  </div>
                </div>

                <div className="info-item">
                  <span className="info-icon">✨</span>
                  <div className="info-text">
                    <strong>Get full access instantly</strong>
                    <p>All trial restrictions will be removed</p>
                  </div>
                </div>

                <div className="info-item">
                  <span className="info-icon">🎯</span>
                  <div className="info-text">
                    <strong>Unlock all features</strong>
                    <ul>
                      <li>Unlimited photos per event (up to plan limit)</li>
                      <li>More attendees per event</li>
                      <li>Custom watermarks</li>
                      <li>Full plan benefits</li>
                    </ul>
                  </div>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  ⚠️ {error}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowUpgradeModal(false)}
                disabled={upgrading}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm"
                onClick={handleConfirmUpgrade}
                disabled={upgrading}
              >
                {upgrading ? 'Processing...' : 'Confirm Upgrade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TrialBanner;
